import { MDBBtn, MDBCol, MDBRow } from "mdb-react-ui-kit";
import Image from "next/image";
import { useRouter } from "next/router";
import { URLS } from "@/utils/constants";
import { forwardRef, ReactElement, useEffect, useMemo, useState } from "react";
import SwiperClass from "swiper";
import {
    sortByPriority,
    sortToursByPriorities,
} from "@/utils/common/sortByPriority";
import styles from "../../../styles/navbar/DropdownMenu.module.scss";
import userInterfaceSvg from "../../../utils/svg-icon/userInterface";
import { DropDownItems } from "../../../models/common/enums";
import CardTour from "../../home/cards/cardTour/CardTour";
import ButtonBack from "../carousel/ButtonBack";
import ButtonNext from "../carousel/ButtonNext";
import Carousel from "../carousel/Carousel";
import { ToursByTourTypeEntity, ToursEntity } from "../../../models/home";
import { TBreackpointsCarousel } from "../../../models/common/common";

type TagDestination = {
    _id: string;
    title: string;
    tagDestination: string;
    image?: { original?: string };
    body?: string;
    tour?: ToursEntity[];
    priority?: number;
    tourPriorities?: Array<{ tourId: string; priority: number }>;
};

// TODO: Remove eslint disable
const DropdownMenu = forwardRef<
    HTMLDivElement,
    {
        dropdownChoice: DropDownItems;
        dropdownDataDestinations: TagDestination[];
        dropdownDataTours: ToursByTourTypeEntity[];
        onMouseLeave: () => void;
    }
>(
    (
        {
            dropdownChoice,
            dropdownDataDestinations,
            dropdownDataTours,
            onMouseLeave,
        },
        ref
        // eslint-disable-next-line sonarjs/cognitive-complexity
    ): ReactElement => {
        const [swiperClass, setSwiperClass] = useState<SwiperClass | null>(
            null
        );

        const path = URLS.allTours;
        const [optionHover, setOptionHover] = useState<number>(0);
        const [loadMore, setLoadMore] = useState<number>(0);
        const [coverError, setCoverError] = useState<boolean>(false);

        const router = useRouter();

        const dropdownData: Array<ToursByTourTypeEntity | TagDestination> =
            useMemo(() => {
                const data =
                    dropdownChoice === DropDownItems.TOURS
                        ? dropdownDataTours
                        : dropdownDataDestinations;
                return (data ?? []) as Array<
                    ToursByTourTypeEntity | TagDestination
                >;
            }, [dropdownChoice, dropdownDataTours, dropdownDataDestinations]);

        const selectedDropdownOption:
            | ToursByTourTypeEntity
            | TagDestination
            | undefined = useMemo(() => {
            return dropdownData[optionHover];
        }, [dropdownData, optionHover]);

        const options = useMemo(() => {
            return dropdownData.map(item => {
                if (dropdownChoice === DropDownItems.TOURS) {
                    return (item as ToursByTourTypeEntity).tourType;
                }
                return (item as TagDestination).title;
            });
        }, [dropdownData, dropdownChoice]);

        const hasOtherItemsToShow = () => {
            return options.slice(loadMore + 5).length > 0;
        };

        useEffect(() => {
            setLoadMore(0);
            setOptionHover(0);
        }, [dropdownChoice]);

        const visibleCards = useMemo(() => {
            if (dropdownChoice === DropDownItems.TOURS) {
                const opt = selectedDropdownOption as
                    | ToursByTourTypeEntity
                    | undefined;
                return (
                    opt?.tours?.map((element: ToursEntity) => (
                        <CardTour
                            className={`mx-2 mx-md-0 ${styles.dropDownCarouselCard}`}
                            classNameBody={styles.dropDownCarouselCardBody}
                            key={element._id} // eslint-disable-line no-underscore-dangle
                            content={element}
                            isSmall
                            height="40%"
                        />
                    )) ?? []
                );
            }
            if (dropdownChoice === DropDownItems.DESTINATIONS) {
                const opt = selectedDropdownOption as
                    | TagDestination
                    | undefined;
                // Usa tourPriorities dalla risposta API se disponibile, altrimenti fallback a tour.priority
                const sortedTours =
                    opt?.tourPriorities && opt.tourPriorities.length > 0
                        ? sortToursByPriorities(
                              opt.tourPriorities,
                              opt?.tour ?? []
                          )
                        : sortByPriority(
                              tour =>
                                  tour.priority ? -tour.priority : undefined,
                              opt?.tour ?? []
                          );
                return sortedTours.map((element: ToursEntity) => (
                    <CardTour
                        className={`mx-2 mx-md-0 ${styles.dropDownCarouselCard}`}
                        classNameBody={styles.dropDownCarouselCardBody}
                        key={element._id} // eslint-disable-line no-underscore-dangle
                        content={element}
                        isSmall
                        height="40%"
                    />
                ));
            }
            return [];
        }, [selectedDropdownOption, dropdownChoice]);

        const handleRouting = () => {
            const queryParams: string[] = [];
            if (dropdownChoice === DropDownItems.TOURS) {
                const tourType = (
                    selectedDropdownOption as ToursByTourTypeEntity
                )?.type;
                if (tourType) {
                    queryParams.push(
                        `tourType=${encodeURIComponent(tourType)}`
                    );
                }
            }
            if (dropdownChoice === DropDownItems.DESTINATIONS) {
                const tagDestination = (
                    selectedDropdownOption as TagDestination
                )?.tagDestination;
                if (tagDestination) {
                    // Trasforma "--" in "-and-" per usare "and" nell'URL invece di "&"
                    const transformedTag = tagDestination.replace(
                        /--/g,
                        "-and-"
                    );
                    queryParams.push(
                        `tagDestination=${encodeURIComponent(transformedTag)}`
                    );
                }
            }
            const queryString =
                queryParams.length > 0 ? `?${queryParams.join("&")}` : "";
            const fullUrl = `${path}${queryString}`;

            if (router.pathname !== path) {
                router.push(fullUrl);
            } else {
                router.replace(fullUrl).then(() => router.reload());
            }
        };

        const breackPointCarousel: TBreackpointsCarousel = {
            0: {
                slidesPerView: 1.3,
                spaceBetween: 32,
                height: 530,
            },
            576: {
                slidesPerView: 1.3,
                spaceBetween: 32,
                height: 530,
            },
            768: {
                slidesPerView: 2,
                autoHeight: true,
                width: 200,
            },
            992: {
                slidesPerView: 1,
                autoHeight: true,
                width: 220,
                spaceBetween: 32,
            },
            1200: {
                slidesPerView: 1,
                autoHeight: true,
                width: 220,
                spaceBetween: 32,
            },
        };

        return (
            <div
                className={`${styles.dropdownContainer} position-absolute w-100`}
                onMouseLeave={onMouseLeave}
            >
                <div
                    ref={ref}
                    className={`${styles.dropdownContent} bg-light container-fluid`}
                >
                    <MDBRow className="h-100">
                        <MDBCol
                            size={3}
                            className={`${styles.dropdownColumnLeft} position-relative `}
                        >
                            {(() => {
                                const defaultCover =
                                    "/images/tours/across-italy-reduced.jpg";
                                let rawCover = defaultCover;
                                if (!coverError) {
                                    if (
                                        dropdownChoice === DropDownItems.TOURS
                                    ) {
                                        rawCover =
                                            (
                                                selectedDropdownOption as ToursByTourTypeEntity
                                            )?.image || defaultCover;
                                    } else {
                                        rawCover =
                                            (
                                                selectedDropdownOption as TagDestination
                                            )?.image?.original || defaultCover;
                                    }
                                }
                                const normalizedCover =
                                    rawCover.startsWith("http") ||
                                    rawCover.startsWith("/")
                                        ? rawCover
                                        : `/${rawCover}`;
                                const altText =
                                    dropdownChoice === DropDownItems.TOURS
                                        ? (
                                              selectedDropdownOption as ToursByTourTypeEntity
                                          )?.tourType || "tour-image"
                                        : (
                                              selectedDropdownOption as TagDestination
                                          )?.title || "tour-image";
                                return (
                                    <Image
                                        alt={altText}
                                        src={normalizedCover}
                                        fill
                                        className={styles.dropdownColumnLeft}
                                        onError={() => setCoverError(true)}
                                    />
                                );
                            })()}
                            <div
                                className={`d-flex flex-column justify-content-end  px-3 ${styles.dropdownInnerColumn}`}
                            >
                                <MDBBtn
                                    className={`${styles.dropdownColumnLeftButton} rounded-9`}
                                    onClick={() => {
                                        router
                                            .push(
                                                {
                                                    pathname: path,
                                                    query: {
                                                        tourType: null,
                                                        destination: null,
                                                    },
                                                },
                                                path,
                                                { shallow: true }
                                            )
                                            .then(() => router.reload());
                                    }}
                                >
                                    {"Discover all of our tours"}{" "}
                                    <div className="ps-3">
                                        <userInterfaceSvg.StarFilled />
                                    </div>
                                </MDBBtn>
                            </div>
                        </MDBCol>
                        <MDBCol size={6} className="bg-light p-0">
                            <MDBRow className="h-100 g-0">
                                <MDBCol
                                    size={6}
                                    className={styles.dropdownInnerColumn}
                                >
                                    <div
                                        className={`border-end d-flex flex-column justify-content-between h-100 ${styles.dropdownColumnContent}`}
                                    >
                                        <div>
                                            <div
                                                className="pt-0"
                                                style={{ marginTop: "-1.8rem" }}
                                            >
                                                {options
                                                    .slice(
                                                        loadMore,
                                                        loadMore + 5
                                                    )
                                                    .map(
                                                        (
                                                            el: string,
                                                            i: number
                                                        ) => {
                                                            return (
                                                                /* eslint-disable react/no-array-index-key */
                                                                <div
                                                                    key={`${el}-${i}`}
                                                                    className={
                                                                        styles.dropdownColumnOptions
                                                                    }
                                                                    onMouseEnter={() =>
                                                                        setOptionHover(
                                                                            i +
                                                                                loadMore
                                                                        )
                                                                    }
                                                                    onClick={
                                                                        handleRouting
                                                                    }
                                                                >
                                                                    <p
                                                                        className={
                                                                            optionHover ===
                                                                            i +
                                                                                loadMore
                                                                                ? styles.titleSelected
                                                                                : ""
                                                                        }
                                                                    >
                                                                        {el}
                                                                    </p>
                                                                    <userInterfaceSvg.ArrowRightSimpleLine />
                                                                </div>
                                                            );
                                                        }
                                                    )}
                                            </div>
                                        </div>
                                        <div
                                            className={
                                                styles.dropdownColumnCenterButtonContainer
                                            }
                                        >
                                            {options.length > 5 && (
                                                <div className="d-flex justify-content-between ">
                                                    <MDBBtn
                                                        className={`${
                                                            styles.dropdownColumnCenterButton
                                                        } 
                                                                    ${
                                                                        hasOtherItemsToShow()
                                                                            ? "d-flex"
                                                                            : "invisible"
                                                                    }
                                                                    rounded-9 w-50`}
                                                        onClick={() =>
                                                            setLoadMore(
                                                                () =>
                                                                    loadMore + 5
                                                            )
                                                        }
                                                    >
                                                        Load More
                                                    </MDBBtn>
                                                    <MDBBtn
                                                        tag="a"
                                                        floating
                                                        className={`${
                                                            styles.dropdownColumnCenterBackButton
                                                        } ${
                                                            loadMore > 0
                                                                ? "d-flex"
                                                                : "d-none "
                                                        } shadow-0`}
                                                        onClick={() =>
                                                            setLoadMore(
                                                                () =>
                                                                    loadMore - 5
                                                            )
                                                        }
                                                    >
                                                        <userInterfaceSvg.ArrowLeftLine />
                                                    </MDBBtn>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </MDBCol>
                                <MDBCol
                                    size={6}
                                    className={styles.dropdownInnerColumn}
                                >
                                    <div
                                        className={`${styles.dropdownColumnContent} d-flex flex-column justify-content-between h-100 `}
                                    >
                                        <div>
                                            <h4
                                                className={
                                                    styles.dropdownColumnTitle
                                                }
                                            >
                                                {selectedDropdownOption?.title?.replace(
                                                    /^Description\s*-\s*/i,
                                                    ""
                                                )}
                                            </h4>
                                            <p
                                                className={`${styles.dropdownColumnTitle} mb-0`}
                                                style={{
                                                    fontSize: "1.2rem",
                                                    fontFamily:
                                                        "GeneralSans-Semibold",
                                                    color: "#ed6f6f",
                                                }}
                                            >
                                                {dropdownChoice ===
                                                DropDownItems.TOURS
                                                    ? (() => {
                                                          const tourType = (
                                                              selectedDropdownOption as ToursByTourTypeEntity
                                                          )?.type;

                                                          switch (tourType) {
                                                              case "city":
                                                                  return "3-5 days";
                                                              case "region":
                                                                  return "3-9 days";
                                                              case "italy":
                                                                  return "6-17 days";
                                                              default:
                                                                  return "Scopri i nostri tour guidati";
                                                          }
                                                      })()
                                                    : ""}
                                            </p>
                                            <div className="pt-4">
                                                <p
                                                    className={
                                                        styles.columnParagraph
                                                    }
                                                >
                                                    {dropdownChoice ===
                                                    DropDownItems.TOURS
                                                        ? (
                                                              selectedDropdownOption as ToursByTourTypeEntity
                                                          )?.body
                                                        : (
                                                              selectedDropdownOption as TagDestination
                                                          )?.body || ""}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="d-flex justify-content-center">
                                            <MDBBtn
                                                className={`${styles.dropdownColumnCenterButton} rounded-9`}
                                                onClick={handleRouting}
                                            >
                                                View More
                                            </MDBBtn>
                                        </div>
                                    </div>
                                </MDBCol>
                            </MDBRow>
                        </MDBCol>
                        <MDBCol
                            size={3}
                            className={`${styles.dropdownColumnRight} `}
                        >
                            <MDBRow className="h-100">
                                <MDBCol className={styles.dropdownInnerColumn}>
                                    <div
                                        className="h-full"
                                        style={{ position: "relative" }}
                                    >
                                        <div
                                            className={`${styles.dropdownColumnContent} d-flex justify-content-between`}
                                        >
                                            <h4
                                                className={
                                                    styles.dropdownColumnTitle
                                                }
                                            >
                                                Our Tours
                                            </h4>
                                            {visibleCards.length > 1 && (
                                                <div className="d-flex mt-3 mb-3">
                                                    <ButtonBack
                                                        swiperClass={
                                                            swiperClass
                                                        }
                                                    />
                                                    <ButtonNext
                                                        className="ms-2"
                                                        swiperClass={
                                                            swiperClass
                                                        }
                                                    />
                                                </div>
                                            )}
                                        </div>
                                        <div
                                            style={{
                                                position: "relative",
                                                right: -1,
                                            }}
                                        >
                                            <Carousel
                                                renderContainer
                                                classNameContainer="d-flex justify-content-center align-items-center h-full"
                                                breakpoints={
                                                    breackPointCarousel
                                                }
                                                setSwiperClass={setSwiperClass}
                                            >
                                                {visibleCards}
                                            </Carousel>
                                        </div>
                                    </div>
                                </MDBCol>
                            </MDBRow>
                        </MDBCol>
                    </MDBRow>
                </div>
            </div>
        );
    }
);

export default DropdownMenu;
